import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { api } from '../services/api'

interface Campaign {
  id: string
  name: string
  status: string
  description?: string
  startDate?: string
  endDate?: string
  contactsCount: number
  callsCount: number
  joinedAt?: string
  role?: string
  // ✅ Quick SMS feature
  quickSmsEnabled?: boolean
  quickSmsTemplateId?: string
  quickSmsTemplate?: {
    id: string
    name: string
    content: string
    category?: string
  }
  // ✅ Quick Email feature
  quickEmailEnabled?: boolean
  quickEmailTemplateId?: string
  quickEmailTemplate?: {
    id: string
    name: string
    subject: string
    content: string
    category?: string
  }
}

interface CampaignContextType {
  campaigns: Campaign[]
  activeCampaign: Campaign | null
  setActiveCampaign: (campaign: Campaign | null) => void
  isLoading: boolean
  refreshCampaigns: () => Promise<void>
}

const CampaignContext = createContext<CampaignContextType | undefined>(undefined)

interface CampaignProviderProps {
  children: ReactNode
}

export function CampaignProvider({ children }: CampaignProviderProps) {
  const { user } = useAuth()
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [activeCampaign, setActiveCampaignState] = useState<Campaign | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  // Fetch assigned campaigns for agents
  const fetchMyCampaigns = async () => {
    if (!user) return
    
    // Only agents have assigned campaigns, admins see all campaigns
    if (user.role !== 'AGENT') return

    try {
      setIsLoading(true)
      const response = await api.get('/campaigns/my-campaigns', {
        headers: {
          'X-Background-Request': 'true' // Prevent loading modal
        }
      })
      const data = response.data
      setCampaigns(data)

      // Auto-select first campaign if none selected
      if (data.length > 0 && !activeCampaign) {
        const storedId = localStorage.getItem('activeCampaignId')
        const stored = data.find((c: Campaign) => c.id === storedId)
        setActiveCampaignState(stored || data[0])
      }
    } catch (error) {
      setCampaigns([])
    } finally {
      setIsLoading(false)
    }
  }

  // Fetch campaigns when user changes
  useEffect(() => {
    fetchMyCampaigns()
  }, [user?.id])

  // Save active campaign to localStorage
  const setActiveCampaign = (campaign: Campaign | null) => {
    setActiveCampaignState(campaign)
    if (campaign) {
      localStorage.setItem('activeCampaignId', campaign.id)
    } else {
      localStorage.removeItem('activeCampaignId')
    }
  }

  const value: CampaignContextType = {
    campaigns,
    activeCampaign,
    setActiveCampaign,
    isLoading,
    refreshCampaigns: fetchMyCampaigns
  }

  return (
    <CampaignContext.Provider value={value}>
      {children}
    </CampaignContext.Provider>
  )
}

export function useCampaign() {
  const context = useContext(CampaignContext)
  if (context === undefined) {
    throw new Error('useCampaign must be used within CampaignProvider')
  }
  return context
}
